import React from "react";
import { Popover } from "@mui/material";
import ColorPicker from "./ColorPicker/ColorPicker";
import { useState } from "react";
import { Checkbox } from "@mui/material";
import { styled } from "@mui/material/styles";
import { TextField, MenuItem } from "@mui/material";
import { useEffect } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { createOrUpdateShape } from "orion-components/GlobalData/Actions";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate } from "orion-components/i18n";

const CustomizedTextField = styled((props) => <TextField {...props} />)(
	({ width, height, paddingLeft, paddingRight, dir }) => ({
		"& label": {
			color: "#9A9FA3",
			fontSize: 10
		},
		"& label.Mui-focused": {
			color: "#9A9FA3"
		},
		"& .MuiInputBase-input": {
			...(dir === "ltr" && { paddingLeft: `${paddingLeft}!important`, paddingRight: "0px !important" }),
			...(dir === "rtl" && { paddingRight: `${paddingRight}!important`, paddingLeft: "0px !important" })
		},
		"& .MuiOutlinedInput-root": {
			fontSize: 10,
			color: "#FFFFFF",
			fontFamily: "Roboto",
			width: width || 59, // default to 59 if width prop is not provided
			height: height || 30, // default to 30 if height prop is not provided
			"& fieldset": {
				borderColor: "#5E6571"
			},
			"&:hover fieldset": {
				borderColor: "#4DB5F4"
			},
			"&.Mui-focused fieldset": {
				borderColor: "#4DB5F4"
			}
		}
	})
);

const StyledArrowDropDownIcon = styled(ArrowDropDownIcon)({
	fontSize: "18px",
	right: "2px !important"
});

const StyledMenuItem = styled(MenuItem)({
	width: "70px",
	fontSize: "11px"
});

const propTypes = {
	defaultStrokeColor: PropTypes.string,
	shapeType: PropTypes.string,
	defaultLineType: PropTypes.string,
	defaultLineWidth: PropTypes.string
};

function isValidHexaDecimal(hexString) {
	const regex = /^#?([a-f0-9]{6}|[a-f0-9]{3})$/i;
	return regex.test(hexString);
}

let lineStrokeOpacity = null;

const StrokeProperties = ({ shapeType }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [point, setPoint] = useState(2);
	const [strokeType, setStrokeType] = useState("Dashed");
	const [color, setColor] = useState("");
	const [inputColor, setInputColor] = useState("");
	const shapesReducer = useSelector((state) => state.shapes);
	const dir = useSelector((state) => getDir(state));

	useEffect(() => {
		setColor(shapesReducer[shapeType]?.polyStroke);
		setInputColor(shapesReducer[shapeType]?.polyStroke);
		setStrokeType(shapesReducer[shapeType]?.lineType);
		setPoint(shapesReducer[shapeType]?.lineWidth);
		if (
			shapeType === "lineString" ||
			shapeType === "polygon" ||
			shapeType === "rectangle" ||
			shapeType === "circle"
		) {
			EventBus.publish("drawUpdateStyles", shapesReducer[shapeType]);
		}
	}, [shapesReducer]);

	const dispatch = useDispatch();

	const syncColor = (colorCode) => {
		setColor(colorCode);
		if (
			shapeType === "lineString" ||
			shapeType === "polygon" ||
			shapeType === "rectangle" ||
			shapeType === "circle"
		) {
			EventBus.publish("drawUpdateStyles", { ...shapesReducer[shapeType], polyStroke: colorCode });
		}
	};

	const handlePopoverOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
	};

	const openPopover = Boolean(anchorEl);

	if (shapesReducer[shapeType]?.lineStrokeOpacity) {
		if (!isNaN(shapesReducer[shapeType].lineStrokeOpacity)) {
			lineStrokeOpacity = shapesReducer[shapeType].lineStrokeOpacity;
		} else {
			lineStrokeOpacity = 100;
		}
	} else {
		//Shapes that were created using the old shape editor do not have a line stroke opacity property. To avoid displaying its value as NaN, we are setting it to the default line stroke value of 100
		lineStrokeOpacity = 100;
	}

	return (
		<div>
			<div
				style={{
					display: "flex",
					position: "relative" // Add this style to enable positioning for Popover
				}}
			>
				<div
					aria-owns={openPopover ? "stroke-mouse-over-popover" : undefined}
					aria-haspopup="true"
					onMouseEnter={handlePopoverOpen}
					style={{
						width: 35,
						height: 35,
						backgroundColor: color,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 5,
						opacity: 1
					}}
				>
					<img
						src={require("../icons/icon-stroke.png")}
						style={{ boxShadow: "1px 1px blur 2px #0000004D" }}
					/>
				</div>
				<div
					style={{
						fontSize: 13,
						color: "#FFFFFF",
						letterSpacing: 0,
						opacity: "1",
						...(dir === "ltr" && { marginLeft: 14 }),
						...(dir === "rtl" && { marginRight: 14 }),
						fontFamily: "roboto"
					}}
				>
					<p>
						<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.strokeColor" />
					</p>
					<p>
						{point}{" "}
						<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.pt" />{" "}
						| {strokeType} | {lineStrokeOpacity + "%"}
					</p>
				</div>
			</div>
			<Popover
				id="stroke-mouse-over-popover"
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "top",
					horizontal: "left"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left"
				}}
				open={openPopover}
				onClose={handlePopoverClose}
				PaperProps={{ onMouseLeave: handlePopoverClose }}
				disableRestoreFocus // This prevents focus from leaving the popover content
			>
				<div style={{ width: 285, height: 326, backgroundColor: "#25272B" }}>
					<div style={{ height: 62, width: 283.47, backgroundColor: "#191A1D" }}>
						<div
							style={{
								paddingTop: 10,
								...(dir === "ltr" && { marginLeft: 12 }),
								...(dir === "rtl" && { marginRight: 12 }),
								display: "flex",
								marginBottom: 20,
								alignItems: "center",
								justifyContent: dir === "ltr" ? "left" : "right"
							}}
						>
							<div
								style={{
									...(dir === "ltr" && { marginRight: 5 }),
									...(dir === "rtl" && { marginLeft: 5 })
								}}
							>
								<Checkbox
									checkedIcon={
										<div
											style={{
												width: "22px",
												height: "22px",
												backgroundColor: color,
												display: "flex",
												alignItems: "center",
												justifyContent: "center"
											}}
										>
											<span style={{ color: "#FFFFFF" }}>&#x2713;</span>
										</div>
									}
									checked={true}
									onChange={() => {}}
								/>
							</div>
							<div
								style={{
									...(dir === "ltr" && { marginRight: 10 }),
									...(dir === "rtl" && { marginLeft: 10 })
								}}
							>
								<CustomizedTextField
									variant="outlined"
									name="selectedColor"
									value={inputColor}
									inputProps={{ style: { color: "#FFFFFF", fontSize: 10, fontFamily: "Roboto" } }}
									onChange={(e) => {
										setInputColor(e.target.value);
										if (isValidHexaDecimal(e.target.value)) {
											syncColor(e.target.value);
										}
									}}
									paddingLeft="6px"
									paddingRight="6px"
									dir={dir}
								/>
							</div>
							<div
								style={{
									...(dir === "ltr" && { marginRight: 10 }),
									...(dir === "rtl" && { marginLeft: 10 })
								}}
							>
								<CustomizedTextField
									variant="outlined"
									name="point"
									value={point}
									onChange={(e) => {
										setPoint(e.target.value);
										dispatch(createOrUpdateShape(shapeType, { lineWidth: e.target.value }));
										EventBus.publish("drawUpdateStyles", {
											...shapesReducer[shapeType],
											lineWidth: e.target.value
										});
									}}
									width="70px"
									height="30px"
									paddingLeft="6px"
									paddingRight="6px"
									dir={dir}
									select
									SelectProps={{
										inputProps: {
											IconComponent: StyledArrowDropDownIcon
										},
										MenuProps: {
											anchorOrigin: {
												vertical: "top",
												horizontal: "left"
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left"
											},
											getContentAnchorEl: null
										}
									}}
								>
									<StyledMenuItem value={1} selected={point === 1}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.point1" />
									</StyledMenuItem>
									<StyledMenuItem value={2} selected={point === 2}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.point2" />
									</StyledMenuItem>
									<StyledMenuItem value={3} selected={point === 3}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.point3" />
									</StyledMenuItem>
									<StyledMenuItem value={4} selected={point === 4}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.point4" />
									</StyledMenuItem>
									<StyledMenuItem value={5} selected={point === 5}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.point5" />
									</StyledMenuItem>
									<StyledMenuItem value={6} selected={point === 6}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.point6" />
									</StyledMenuItem>
								</CustomizedTextField>
							</div>
							<div
								style={{
									...(dir === "ltr" && { marginRight: 16 }),
									...(dir === "rtl" && { marginLeft: 16 })
								}}
							>
								<CustomizedTextField
									variant="outlined"
									name="strokeType"
									value={strokeType}
									onChange={(e) => {
										setStrokeType(e.target.value);
										dispatch(createOrUpdateShape(shapeType, { lineType: e.target.value }));
										EventBus.publish("drawUpdateStyles", {
											...shapesReducer[shapeType],
											lineType: e.target.value
										});
									}}
									select
									width="70px"
									height="30px"
									paddingLeft="6px"
									paddingRight="6px"
									dir={dir}
									SelectProps={{
										inputProps: {
											IconComponent: StyledArrowDropDownIcon
										},
										MenuProps: {
											anchorOrigin: {
												vertical: "top",
												horizontal: "left"
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left"
											},
											getContentAnchorEl: null
										}
									}}
								>
									<StyledMenuItem value="Solid" selected={strokeType === "Solid"}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.solid" />
									</StyledMenuItem>
									<StyledMenuItem value="Dashed" selected={strokeType === "Dashed"}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.dashed" />
									</StyledMenuItem>
									<StyledMenuItem value="Dotted" selected={strokeType === "Dotted"}>
										<Translate value="global.sharedComponents.shapeEditor.components.shared.strokeProperties.dotted" />
									</StyledMenuItem>
								</CustomizedTextField>
							</div>
						</div>
					</div>
					<ColorPicker defaultColor={color} syncColor={syncColor} shapeType={shapeType} isForFill={false} />
				</div>
			</Popover>
		</div>
	);
};

StrokeProperties.propTypes = propTypes;

export default StrokeProperties;
